import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, SpacingMode } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';
import { SceneNode } from '../../core/SceneNode';
import * as THREE from 'three';

export interface LayouterParameters {
    [key: string]: any;
}

// Mathematical structures for orientation calculations
export interface FrenetFrame {
    tangent: Vector3;
    normal: Vector3;
    binormal: Vector3;
}

export interface SurfaceFrame {
    uTangent: Vector3;
    vTangent: Vector3;
    normal: Vector3;
}

export abstract class BaseLayouter {
    public readonly name: string;
    public readonly description: string;
    public readonly defaultParameters: Record<string, any>;
    
    // NEW: Layouter classification for spacing mode support
    public readonly layouterDimension: '1d' | '2d';
    public readonly supportedSpacingModes: SpacingMode[];
    
    protected _debugVisualization: THREE.Group | null = null;
    
    constructor(
        name: string, 
        description: string, 
        defaultParameters: Record<string, any> = {},
        layouterDimension: '1d' | '2d' = '1d',
        supportedSpacingModes: SpacingMode[] = [SpacingMode.Fixed]
    ) {
        this.name = name;
        this.description = description;
        this.defaultParameters = defaultParameters;
        this.layouterDimension = layouterDimension;
        this.supportedSpacingModes = supportedSpacingModes;
    }
    
    // Abstract methods that must be implemented by subclasses
    abstract calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3;
    abstract calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3;
    
    // Optional method to calculate uniform scale (for perfect tiling layouters like hexagonal)
    calculateScale?(index: number, totalCount: number, layoutData: LayoutData): number;
    
    // Optional method for layouters to directly apply all transformations to POIs
    // This is more efficient and cleaner than the calculate* methods
    applyToObjects?(pois: any[], layoutData: LayoutData): void;
    
    // Validation method (can be overridden by subclasses)
    validateParameters(layoutData: LayoutData): boolean {
        // Basic validation - check if layouter name matches
        return layoutData.layouter === this.name;
    }
    
    // Get required parameters for this layouter
    getRequiredParameters(): string[] {
        return Object.keys(this.defaultParameters);
    }
    
    // Get parameter value with fallback to default
    protected getParameter(layoutData: LayoutData, key: string, fallback?: any): any {
        // First check layouter-specific properties
        if (layoutData.layouterProperties && layoutData.layouterProperties[key] !== undefined) {
            return layoutData.layouterProperties[key];
        }
        
        // Then check direct properties on layoutData
        const property = (layoutData as any)[key];
        if (property !== undefined) {
            // Check if it's an InterpolatedProperty (has .value getter)
            if (property && typeof property === 'object' && 'value' in property) {
                return property.value; // 🎯 Get interpolated value!
            }
            return property;
        }
        
        // Finally use default or provided fallback
        return fallback !== undefined ? fallback : this.defaultParameters[key];
    }
    
    // Helper method to normalize index for circular arrangements
    protected normalizeIndex(index: number, totalCount: number): number {
        return Math.max(0, Math.min(index, totalCount - 1));
    }
    
    // Helper method to calculate progress (0 to 1) for index
    protected calculateProgress(index: number, totalCount: number): number {
        if (totalCount <= 1) return 0;
        return index / (totalCount - 1);
    }
    
    // Helper method to convert degrees to radians
    protected degreesToRadians(degrees: number): number {
        return degrees * Math.PI / 180;
    }
    
    // Helper method to convert radians to degrees
    protected radiansToDegrees(radians: number): number {
        return radians * 180 / Math.PI;
    }
    
    // ==================== MATHEMATICAL FOUNDATION ====================
    
    /**
     * Calculate Frenet frame for 1D curves (tangent, normal, binormal)
     * This implements the mathematical foundation from the alignment documentation
     */
    protected calculateFrenetFrame(
        getPositionAt: (t: number) => Vector3,
        t: number,
        totalCount: number
    ): FrenetFrame {
        const dt = 0.001;
        
        // Calculate tangent vector (first derivative)
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = getPositionAt(t1);
        const p2 = getPositionAt(t2);
        const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
        
        // Calculate normal vector (second derivative, curvature direction)
        const tangent1 = this.calculateTangentAt(getPositionAt, t1);
        const tangent2 = this.calculateTangentAt(getPositionAt, t2);
        let normal = new Vector3(tangent2.x - tangent1.x, tangent2.y - tangent1.y, tangent2.z - tangent1.z);
        
        // Handle degenerate case (straight line)
        if (normal.length() < 0.001) {
            // Use a perpendicular vector to tangent
            if (Math.abs(tangent.y) < 0.9) {
                normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));
            } else {
                normal = new Vector3().crossVectors(tangent, new Vector3(1, 0, 0));
            }
        }
        normal.normalize();
        
        // Calculate binormal vector (tangent × normal)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        return { tangent, normal, binormal };
    }
    
    /**
     * Helper to calculate tangent at specific parameter t
     */
    private calculateTangentAt(getPositionAt: (t: number) => Vector3, t: number): Vector3 {
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = getPositionAt(t1);
        const p2 = getPositionAt(t2);
        return new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
    }
    
    /**
     * Calculate surface frame for 2D surfaces (u-tangent, v-tangent, normal)
     * This implements the mathematical foundation from the alignment documentation
     */
    protected calculateSurfaceFrame(
        getPositionAt: (u: number, v: number) => Vector3,
        u: number,
        v: number
    ): SurfaceFrame {
        const du = 0.001;
        const dv = 0.001;
        
        // Calculate U-tangent (partial derivative ∂P/∂u)
        const u1 = Math.max(0, u - du);
        const u2 = Math.min(1, u + du);
        const pu1 = getPositionAt(u1, v);
        const pu2 = getPositionAt(u2, v);
        const uTangent = new Vector3(pu2.x - pu1.x, pu2.y - pu1.y, pu2.z - pu1.z).normalize();
        
        // Calculate V-tangent (partial derivative ∂P/∂v)
        const v1 = Math.max(0, v - dv);
        const v2 = Math.min(1, v + dv);
        const pv1 = getPositionAt(u, v1);
        const pv2 = getPositionAt(u, v2);
        const vTangent = new Vector3(pv2.x - pv1.x, pv2.y - pv1.y, pv2.z - pv1.z).normalize();
        
        // Calculate surface normal (U-tangent × V-tangent)
        const normal = new Vector3().crossVectors(uTangent, vTangent).normalize();
        
        return { uTangent, vTangent, normal };
    }
    
    // ==================== ALIGNMENT SYSTEM IMPLEMENTATION ====================
    
    /**
     * Determine forward direction based on alignment mode and surface type
     * Implements the logic from the alignment documentation
     */
    protected determineForwardDirection(
        alignmentMode: AlignmentMode,
        frenetFrame?: FrenetFrame,
        surfaceFrame?: SurfaceFrame
    ): Vector3 {
        if (frenetFrame) {
            // 1D curves (line, circle, spiral)
            if (alignmentMode === AlignmentMode.Aligned) {
                // ALIGNED: Object follows the curve (uses normal - curvature direction)
                return frenetFrame.normal.clone();
            } else {
                // PERPENDICULAR: Object is perpendicular to curve (uses tangent - movement direction)
                return frenetFrame.tangent.clone();
            }
        } else if (surfaceFrame) {
            // 2D surfaces (grid plane, sphere)
            if (alignmentMode === AlignmentMode.Aligned) {
                // ALIGNED: Object points away from surface (uses surface normal)
                return surfaceFrame.normal.clone();
            } else {
                // PERPENDICULAR: Object is tangent to surface (uses U-tangent)
                return surfaceFrame.uTangent.clone();
            }
        }
        
        // Fallback: face forward
        return new Vector3(0, 0, 1);
    }
    
    /**
     * Determine up direction based on up vector mode and surface type
     * Implements the logic from the alignment documentation
     */
    protected determineUpDirection(
        upVectorMode: UpVectorMode,
        frenetFrame?: FrenetFrame,
        surfaceFrame?: SurfaceFrame
    ): Vector3 {
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Always use world Y-axis
            return new Vector3(0, 1, 0);
        } else {
            // PATH UP: Follow the geometry
            if (frenetFrame) {
                // 1D curves: Use binormal (negated for correct orientation as per docs)
                return frenetFrame.binormal.clone().negate();
            } else if (surfaceFrame) {
                // 2D surfaces: Use V-tangent (negated for correct orientation as per docs)
                return surfaceFrame.vTangent.clone().negate();
            }
        }
        
        // Fallback: world up
        return new Vector3(0, 1, 0);
    }
    
    /**
     * Build orthogonal coordinate system ensuring all axes are perpendicular
     * Implements the algorithm from the alignment documentation
     */
    protected buildOrthogonalSystem(desiredForward: Vector3, desiredUp: Vector3, upVectorMode: UpVectorMode): {
        finalRight: Vector3,
        finalUp: Vector3,
        finalForward: Vector3
    } {
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up-vector is FIXED on (0,1,0)
            finalUp = new Vector3(0, 1, 0);
            
            // Right = Up × Forward
            finalRight = new Vector3().crossVectors(finalUp, desiredForward);
            
            // Check for parallelism (degenerative case)
            if (finalRight.length() < 0.001) {
                finalRight = new Vector3(1, 0, 0); // Fallback
            }
            finalRight.normalize();
            
            // Forward = Right × Up (ensure orthogonality)
            finalForward = new Vector3().crossVectors(finalRight, finalUp);
            finalForward.normalize();
            
        } else {
            // PATH UP: Forward/Up follow the geometry
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Right = Up × Forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Up = Forward × Right (correct for orthogonality)
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        return { finalRight, finalUp, finalForward };
    }
    
    // ==================== ABSTRACT ORIENTATION CALCULATION ====================
    
    /**
     * Calculate orientation quaternion for this specific layouter type
     * Each subclass implements its own orientation logic using the mathematical framework
     */
    protected abstract calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion;
    
    /**
     * Convert orientation to legacy Euler angles for old system compatibility
     * Uses the subclass-specific orientation calculation
     */
    protected orientationToEulerAngles(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Vector3 {
        const quaternion = this.calculateOrientationQuaternion(
            position, index, totalCount, layoutData
        );
        
        return this.quaternionToEulerAngles(quaternion);
    }
    
    // ==================== UTILITY METHODS ====================
    
    /**
     * Convert quaternion to Euler angles (degrees) for legacy compatibility
     */
    private quaternionToEulerAngles(quaternion: Quaternion): Vector3 {
        const eulerRadians = quaternion.toEulerAngles();
        return new Vector3(
            this.radiansToDegrees(eulerRadians.x),
            this.radiansToDegrees(eulerRadians.y),
            this.radiansToDegrees(eulerRadians.z)
        );
    }
    
    // ==================== NEW SPACING SYSTEM HELPERS ====================
    
    /**
     * Determine if the current spacing mode is supported by this layouter
     */
    protected isSpacingModeSupported(layoutData: LayoutData): boolean {
        const spacingMode = this.getParameter(layoutData, 'spacingMode', SpacingMode.Fixed);
        return this.supportedSpacingModes.includes(spacingMode);
    }
    
    /**
     * Get the effective spacing for this layouter based on spacing mode
     */
    protected getEffectiveSpacing(layoutData: LayoutData, objectCount: number): number {
        const spacingMode = this.getParameter(layoutData, 'spacingMode', SpacingMode.Fixed);
        
        if (spacingMode === SpacingMode.Fixed) {
            // Use fixed spacing parameter specific to this layouter
            return this.getFixedSpacingParameter(layoutData);
        } else if (spacingMode === SpacingMode.Distribute) {
            // Calculate uniform spacing based on structure size and object count
            return this.calculateUniformSpacing(layoutData, objectCount);
        }
        
        // Fallback to fixed spacing
        return this.getFixedSpacingParameter(layoutData);
    }
    
    /**
     * Get the fixed spacing parameter for this specific layouter type
     * Must be implemented by each subclass to return their specific parameter
     */
    protected abstract getFixedSpacingParameter(layoutData: LayoutData): number;
    
    /**
     * Calculate uniform spacing based on structure size
     * Must be implemented by subclasses that support uniform spacing
     * Should throw error or return fixed spacing for layouters that don't support uniform spacing
     */
    protected abstract calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number;
    
    // Factory method
    static create(name: string): BaseLayouter {
        throw new Error('BaseLayouter.create() must be implemented by LayouterFactory');
    }
    
    // Clone method
    clone(): BaseLayouter {
        // This should be overridden by subclasses for proper cloning
        throw new Error('clone() method must be implemented by subclasses');
    }
    
    // Serialize layouter configuration
    serialize(): any {
        return {
            name: this.name,
            description: this.description,
            defaultParameters: { ...this.defaultParameters }
        };
    }

    /**
     * Create debug visualization for the layout path/curve
     * Override in subclasses to provide specific visualization
     */
    public createDebugVisualization(layoutData: LayoutData): THREE.Group | null {
        // Base implementation returns null - subclasses override
        return null;
    }

    /**
     * Update debug visualization visibility
     */
    protected updateDebugVisualization(layoutData: LayoutData): void {
        if (!this._debugVisualization) {
            this._debugVisualization = this.createDebugVisualization(layoutData);
        }
        
        if (this._debugVisualization) {
            // Show debug visualization if bounds are shown (reuse existing debug flag)
            const showDebug = false; // TODO: Connect to actual debug setting
            this._debugVisualization.visible = showDebug;
        }
    }

    /**
     * Add debug visualization to scene
     */
    protected addDebugToScene(scene: any): void {
        if (this._debugVisualization && scene && scene.add) {
            scene.add(this._debugVisualization);
        }
    }

    /**
     * Remove debug visualization from scene
     */
    protected removeDebugFromScene(scene: any): void {
        if (this._debugVisualization && scene && scene.remove) {
            scene.remove(this._debugVisualization);
            this._debugVisualization = null;
        }
    }
} 